import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { eventPlanApi, eventsApi, shoppingApi } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/shared/Toast";
import SavingNotice from "../components/shared/SavingNotice";
import useMinLoad from "../hooks/useMinLoad";

function TravelSection({ eyebrow, title, subtitle, children }) {
  return (
    <section className="travel-section">
      <div className="travel-section-header">
        {eyebrow && <div className="travel-section-eyebrow">{eyebrow}</div>}
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function parseLines(value) {
  return (value || "").split("\n").map((item) => item.trim()).filter(Boolean);
}

function formatDateLabel(value) {
  if (!value) return "";
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", {
      weekday: "short",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

function normalizeDateInput(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function parseLocalDate(value) {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function daysUntilDate(value) {
  const date = parseLocalDate(value);
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((date - today) / (1000 * 60 * 60 * 24));
}

function TextList({ items, empty = "Nothing here yet." }) {
  const list = (items || []).filter(Boolean);
  if (list.length === 0) return <p style={{ fontSize: 14, color: "var(--ink-muted)", margin: 0 }}>{empty}</p>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {list.map((item) => (
        <div key={item} style={{ fontSize: 14, color: "var(--ink)", lineHeight: 1.5 }}>{item}</div>
      ))}
    </div>
  );
}

const EVENT_TYPE_LABELS = {
  trip: "Trip / Vacation",
  concert: "Concert / Show",
  wedding: "Wedding",
  birthday_party: "Birthday Party",
  graduation_party: "Graduation Party",
  holiday_gathering: "Holiday Gathering",
  game_night: "Game Night",
  anniversary_dinner: "Anniversary",
  baby_shower: "Baby Shower",
  other_event: "Event",
};

const EVENT_TYPE_OPTIONS = Object.entries(EVENT_TYPE_LABELS)
  .filter(([value]) => value !== "trip")
  .map(([value, label]) => ({ value, label }));

function eventTypeLabel(value) {
  return EVENT_TYPE_LABELS[value] || String(value || "event").replace(/_/g, " ");
}

function planningBriefPlaceholder(eventType) {
  const label = eventTypeLabel(eventType).toLowerCase();
  return `What should AI know about this ${label}? Tone, guests, timing, location, constraints, and what matters most. Mention food, shopping, or budget only if they matter.`;
}

function eventTypeOptionsFor(value) {
  if (!value || EVENT_TYPE_LABELS[value]) return EVENT_TYPE_OPTIONS;
  return [
    { value, label: eventTypeLabel(value) },
    ...EVENT_TYPE_OPTIONS,
  ];
}

function cleanEventShoppingItems(items) {
  return (items || [])
    .map((item) => ({
      name: String(item?.name || item?.item || "").trim(),
      quantity: String(item?.quantity || "").trim(),
      section: String(item?.section || item?.category || "other").trim().toLowerCase(),
      source: "occasion",
    }))
    .filter((item) => item.name);
}

const FOOD_INTENT_PATTERN = /\b(food|snack|snacks|drink|drinks|dinner|lunch|brunch|breakfast|meal|menu|potluck|cater|catering|cake|dessert|appetizer|pizza|wine|beer|cocktail|mocktail|hosting|host at home|cook|home-cooked)\b/i;
const BUDGET_INTENT_PATTERN = /\b(budget|\$|cost|spend|paid|ticket|tickets|venue|rental|restaurant|reservation|parking|transport|hotel|lodging|travel|activity|activities|experience)\b/i;
const HOSTED_CONTEXT_PATTERN = /\b(party|gathering|shower|holiday|host|hosting|at home|guests?|rsvp|potluck|catering)\b/i;
const OUTING_CONTEXT_PATTERN = /\b(concert|show|venue|tickets?|reservation|restaurant|travel|trip|hotel|lodging|parking|transport)\b/i;
const CASUAL_CONTEXT_PATTERN = /\b(game|games|movie|hangout|casual|friends?|board game|card game)\b/i;

function eventPlanDisplayRules(meta = {}) {
  const eventType = String(meta?.event_type || "other_event").toLowerCase();
  const label = eventTypeLabel(eventType);
  const title = String(meta?.title || "");
  const location = String(meta?.location || "");
  const notes = String(meta?.notes || "");
  const context = [label, title, location, notes].filter(Boolean).join(" ");
  const isHosted = HOSTED_CONTEXT_PATTERN.test(context);
  const isOuting = OUTING_CONTEXT_PATTERN.test(context);
  const isCasual = CASUAL_CONTEXT_PATTERN.test(context);
  const hasFoodIntent = FOOD_INTENT_PATTERN.test(context);
  return {
    showPantryShopping: hasFoodIntent || (isHosted && !isOuting && !/\banniversary\b/i.test(context)),
    showFoodDrink: hasFoodIntent || (isHosted && !isOuting),
    showBudget: BUDGET_INTENT_PATTERN.test(context) || (isHosted && !isCasual),
    isCasual,
  };
}

function addStarterTask(tasks, seen, task) {
  const clean = String(task.task || "").trim();
  const key = clean.toLowerCase();
  if (!clean || seen.has(key)) return;
  seen.add(key);
  tasks.push({
    task: clean,
    status: task.status || "todo",
    owner: task.owner || "",
    need_by: task.need_by || "",
    category: task.category || "prep",
  });
}

function buildEventStarterPlan(meta = {}) {
  const rules = eventPlanDisplayRules(meta);
  const title = meta?.title || eventTypeLabel(meta?.event_type) || "this event";
  const eventType = String(meta?.event_type || "other_event").toLowerCase();
  const context = [eventTypeLabel(eventType), meta?.title, meta?.location, meta?.notes].filter(Boolean).join(" ").toLowerCase();
  const days = daysUntilDate(meta?.date_label);
  const needBy = meta?.date_label ? formatDateLabel(meta.date_label) : "";
  const isGiftEvent = /\b(birthday|shower|wedding|graduation|anniversary)\b/.test(context);
  const isTicketed = OUTING_CONTEXT_PATTERN.test(context);
  const tasks = [];
  const seen = new Set();

  addStarterTask(tasks, seen, {
    task: meta?.date_label ? "Confirm date and start time" : "Add event date and time",
    status: meta?.date_label ? "done" : "todo",
    need_by: needBy,
    category: "details",
  });
  addStarterTask(tasks, seen, {
    task: meta?.location ? "Confirm location or venue details" : "Add location or venue",
    status: meta?.location ? "done" : "todo",
    need_by: needBy,
    category: "details",
  });
  if (meta?.rsvp_count) {
    addStarterTask(tasks, seen, {
      task: "Review RSVP count and guest notes",
      status: "todo",
      need_by: days !== null && days <= 3 ? "Today" : "Before shopping",
      category: "guests",
    });
  } else {
    addStarterTask(tasks, seen, {
      task: "Send invite or collect RSVPs",
      status: "todo",
      need_by: days !== null && days <= 7 ? "Today" : "This week",
      category: "guests",
    });
  }
  if (isTicketed) {
    addStarterTask(tasks, seen, {
      task: "Confirm tickets, reservation, parking, or transportation",
      status: "todo",
      need_by: days !== null && days <= 2 ? "Today" : "Before event",
      category: "booking",
    });
  }
  if (rules.showFoodDrink) {
    addStarterTask(tasks, seen, {
      task: "Decide food and drink plan",
      status: "todo",
      need_by: days !== null && days <= 3 ? "Today" : "Before shopping",
      category: "food",
    });
    addStarterTask(tasks, seen, {
      task: "Check pantry before buying event supplies",
      status: "todo",
      need_by: "Before shopping",
      category: "shopping",
    });
  }
  if (rules.showBudget) {
    addStarterTask(tasks, seen, {
      task: "Set simple event budget",
      status: "todo",
      need_by: "Before booking or shopping",
      category: "budget",
    });
  }
  if (isGiftEvent) {
    addStarterTask(tasks, seen, {
      task: "Review gift, registry, or card needs",
      status: "todo",
      need_by: "Before event",
      category: "gifts",
    });
  }
  addStarterTask(tasks, seen, {
    task: "Pack or stage day-of items",
    status: "todo",
    need_by: days === 0 ? "Today" : "Day before",
    category: "day_of",
  });

  const readiness = {
    to_book: [
      !meta?.location ? "Location or venue" : null,
      isTicketed ? "Tickets, reservation, parking, or transportation" : null,
      rules.showFoodDrink && /cater|catering|restaurant|reservation/.test(context) ? "Food/catering reservation" : null,
    ].filter(Boolean),
    pending: [
      !meta?.rsvp_count ? "Guest list / RSVPs" : `${meta.rsvp_count} RSVP${meta.rsvp_count === 1 ? "" : "s"} to review`,
      rules.showFoodDrink ? "Food and drink quantities" : null,
      rules.showBudget ? "Budget amount" : null,
    ].filter(Boolean),
    confirmed: [
      meta?.date_label ? `Date: ${formatDateLabel(meta.date_label)}` : null,
      meta?.location ? `Location: ${meta.location}` : null,
    ].filter(Boolean),
    day_of: [
      "Bring essentials",
      rules.showFoodDrink ? "Set out food/drinks and serving items" : null,
      isTicketed ? "Check arrival time and parking" : "Final setup sweep",
    ].filter(Boolean),
  };

  return {
    event_brief: {
      summary: `Starter plan for ${title}. Built from event type, date, location, RSVP, and notes without an AI call.`,
      tone: "",
      good_fit: [],
      watch_outs: [],
      bring: [],
      book_ahead: readiness.to_book,
    },
    prep_checklist: tasks,
    readiness,
    planning_notes: [
      "Starter plan created without AI. Use Generate plan only when you want richer guest flow, menu ideas, or personalized planning.",
    ],
  };
}

function RsvpPills({ meta }) {
  if (!meta?.rsvp_count) return null;
  const items = [
    meta.rsvp_in ? `${meta.guest_count || meta.rsvp_in} yes` : null,
    meta.rsvp_maybe ? `${meta.rsvp_maybe} maybe` : null,
    meta.rsvp_out ? `${meta.rsvp_out} no` : null,
  ].filter(Boolean);
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {items.map((item) => (
        <span key={item} className="badge" style={{ background: "var(--cream)", color: "var(--ink-soft)" }}>{item}</span>
      ))}
    </div>
  );
}

function EventPlanAskBar({ meta, activeTab, planData }) {
  const displayRules = eventPlanDisplayRules(meta);
  const prompts = displayRules.showFoodDrink
    ? ["What should I do next?", "What should I bring?", "What food prep is left?"]
    : ["What should I do next?", "How should the event flow?", "What setup is left?"];
  const eventLabel = meta?.title || meta?.person_name || eventTypeLabel(meta?.event_type) || "this event";

  function openEventAsk(question = "") {
    const clean = String(question || "").trim();
    const contextParts = [
      `Event: ${eventLabel}`,
      meta?.event_date ? `Date: ${meta.event_date}` : null,
      meta?.location ? `Location: ${meta.location}` : null,
      meta?.rsvp_count ? `RSVPs: ${meta.rsvp_in || 0} coming, ${meta.rsvp_maybe || 0} maybe, ${meta.rsvp_out || 0} out` : null,
      planData?.prep_checklist?.length ? `${planData.prep_checklist.length} prep tasks` : null,
      planData?.event_shopping?.pantry_matches?.length ? `${planData.event_shopping.pantry_matches.length} pantry matches` : null,
    ].filter(Boolean).join(" | ");
    window.dispatchEvent(new CustomEvent("hm_open_ask", {
      detail: {
        question: `${clean || `What should I handle next for ${eventLabel}?`} Use this event planning context: ${contextParts}`,
        context: {
          module: "events",
          module_label: "Events & Trips",
          surface: `Event plan ${activeTab || "details"}`,
          event_id: meta?.id,
          event_type: meta?.event_type,
        },
      },
    }));
  }

  return (
    <div className="fd-food-askbar hm-events-askbar">
      <input
        className="fd-food-askbar__input"
        placeholder="✦ Ask about this event..."
        onFocus={() => openEventAsk("")}
        readOnly
      />
      <div className="fd-food-askbar__chips">
        {prompts.map(prompt => (
          <button key={prompt} type="button" className="fd-food-askbar__chip" onClick={() => openEventAsk(prompt)}>
            {prompt}
          </button>
        ))}
      </div>
      <button type="button" className="fd-food-askbar__button" onClick={() => openEventAsk(prompts[0])}>
        ✦ Ask
      </button>
    </div>
  );
}

function EventStarterCard({ meta, planData, saving, loading, onCreateStarter, onGenerate, onOpenPrep }) {
  const starterPlan = buildEventStarterPlan(meta);
  const starterTasks = starterPlan.prep_checklist || [];
  const currentTasks = planData?.prep_checklist || [];
  const hasPlan = Boolean(planData);
  const displayTasks = hasPlan ? currentTasks : starterTasks;
  const readinessSource = hasPlan ? (planData?.readiness || {}) : (starterPlan.readiness || {});
  const readinessCount = Object.values(readinessSource).reduce((sum, items) => sum + (items || []).length, 0);

  return (
    <div className="card mb-16 event-starter-card">
      <div className="travel-tab-content-head" style={{ marginBottom: 10 }}>
        <div>
          <div className="travel-overview-section-label">✦ Starter automation</div>
          <h3 style={{ margin: 0, fontSize: 20 }}>🎬 Event starter plan</h3>
          <p style={{ margin: "4px 0 0", color: "var(--ink-muted)", fontSize: 13 }}>
            Build first from saved event details. Use AI only when you want richer planning.
          </p>
        </div>
        <span className="badge" style={{ background: "var(--green-light)", color: "var(--green)" }}>
          Rules
        </span>
      </div>

      <div className="travel-mobile-stats event-plan-stats" style={{ marginBottom: 12 }}>
        <div className="travel-stat-card">
          <div className="travel-stat-label">Starter tasks</div>
          <div className="travel-stat-value">{hasPlan ? currentTasks.length : starterTasks.length}</div>
          <div className="travel-stat-hint">{hasPlan ? "saved" : "ready"}</div>
        </div>
        <div className="travel-stat-card">
          <div className="travel-stat-label">Readiness</div>
          <div className="travel-stat-value">{readinessCount}</div>
          <div className="travel-stat-hint">checks</div>
        </div>
        <div className="travel-stat-card">
          <div className="travel-stat-label">AI</div>
          <div className="travel-stat-value">{hasPlan ? "Optional" : "Later"}</div>
          <div className="travel-stat-hint">for rich plan</div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
        {displayTasks.slice(0, 4).map((item) => (
          <div key={item.task} className="travel-packing-item" style={{ cursor: "default" }}>
            <span className="trip-template-packing-check" aria-hidden="true">•</span>
            <span className="trip-template-packing-copy">
              <strong>{item.task}</strong>
              {item.need_by ? <em>{item.need_by}</em> : null}
            </span>
          </div>
        ))}
      </div>

      <div className="travel-empty-actions" style={{ justifyContent: "flex-start" }}>
        {hasPlan ? (
          <button type="button" className="btn btn-secondary" onClick={onOpenPrep}>
            Review saved prep
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={onCreateStarter} disabled={saving}>
            {saving ? "Saving..." : "Create starter plan"}
          </button>
        )}
        <button type="button" className="btn btn-ghost" onClick={onGenerate} disabled={loading}>
          {loading ? "Building..." : hasPlan ? "Generate richer plan" : "Use AI instead"}
        </button>
      </div>
    </div>
  );
}

export default function EventPlanPage() {
  const { eventType, eventId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, applyAiUsage } = useAuth();
  const toast = useToast();

  const [meta, setMeta] = useState(null);
  const [travelerProfile, setTravelerProfile] = useState(null);
  const [savedPlanMeta, setSavedPlanMeta] = useState(null);
  const [moneyDraft, setMoneyDraft] = useState({ planned_budget: "", actual_spend: "" });
  const [planData, setPlanData] = useState(null);
  const [briefDraft, setBriefDraft] = useState({
    summary: "",
    tone: "",
    good_fit: "",
    watch_outs: "",
    bring: "",
    book_ahead: "",
    planning_notes: "",
  });
  const [checklistDraft, setChecklistDraft] = useState([]);
  const [readinessDraft, setReadinessDraft] = useState({
    to_book: "",
    pending: "",
    confirmed: "",
    day_of: "",
  });
  const [loading, setLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingShopping, setAddingShopping] = useState(false);
  const [shoppingAdded, setShoppingAdded] = useState(false);
  const [editingBrief, setEditingBrief] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(false);
  const [editingReadiness, setEditingReadiness] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [confirmAiGenerate, setConfirmAiGenerate] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState({
    title: "",
    event_type: "",
    event_date: "",
    location: "",
    notes: "",
  });
  const initialTab = ["details", "overview", "prep", "plan"].includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "details";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [error, setError] = useState("");

  const travelerHighlights = travelerProfile?.highlights?.slice(0, 5) || [];
  const eventLabel = eventTypeLabel(meta?.event_type);
  const displayRules = eventPlanDisplayRules(meta);
  const hasPlan = Boolean(planData);
  const checklistItems = planData?.prep_checklist || [];
  const hasChecklist = checklistItems.length > 0;
  const readinessSections = {
    to_book: planData?.readiness?.to_book || [],
    pending: planData?.readiness?.pending || [],
    confirmed: planData?.readiness?.confirmed || [],
    day_of: planData?.readiness?.day_of || [],
  };
  const hasReadiness = Object.values(readinessSections).some((items) => items.length > 0);
  const hasGuestExperience = Object.values(planData?.guest_experience || {}).some((items) => (items || []).length > 0);
  const hasTimeline = (planData?.timeline || []).length > 0;
  const hasFoodDrink = displayRules.showFoodDrink && Object.values(planData?.food_drink || {}).some((items) => (items || []).length > 0);
  const eventShopping = planData?.event_shopping || {};
  const pantryMatches = displayRules.showPantryShopping ? (eventShopping?.pantry_matches || []) : [];
  const eventShoppingItems = displayRules.showPantryShopping ? cleanEventShoppingItems(eventShopping?.shopping_items) : [];
  const hasEventShopping = Boolean(
    displayRules.showPantryShopping && (
    eventShopping?.servings ||
    pantryMatches.length > 0 ||
    eventShoppingItems.length > 0 ||
    (eventShopping?.prep_notes || []).length > 0
    )
  );
  const hasSupplies = (planData?.supplies || []).length > 0;
  const suppliesCount = planData?.supplies?.length || 0;
  const hasBudget = displayRules.showBudget && (planData?.budget || []).length > 0;
  const hasCommunication = Boolean(planData?.communication);
  const showShoppingNotice = useMinLoad(addingShopping, 700);
  const prepDone = checklistItems.filter((item) => item.status === "done").length;
  const prepTotal = checklistItems.length;
  const timelineCount = planData?.timeline?.length || 0;
  const shoppingCount = eventShoppingItems.length;
  const rsvpSummary = meta?.rsvp_count ? `${meta.rsvp_count} RSVP${meta.rsvp_count === 1 ? "" : "s"}` : "No RSVPs yet";

  useEffect(() => {
    let ignore = false;

    async function loadPlan() {
      setLoadingPlan(true);
      setError("");
      try {
        const data = await eventPlanApi.get(eventType, eventId);
        if (ignore) return;
        setMeta(data.meta || null);
        setTravelerProfile(data.traveler_profile || null);
        setSavedPlanMeta(data.saved_plan || null);
        setMoneyDraft({
          planned_budget: data.saved_plan?.planned_budget ?? "",
          actual_spend: data.saved_plan?.actual_spend ?? "",
        });
        setPlanData(data.saved_plan?.plan_data || null);
        setEditingDetails(false);
      } catch (err) {
        if (!ignore) setError(err.message || "Failed to load event plan.");
      } finally {
        if (!ignore) setLoadingPlan(false);
      }
    }

    loadPlan();
    return () => { ignore = true; };
  }, [eventId, eventType]);

  useEffect(() => {
    setDetailsDraft({
      title: meta?.title || "",
      event_type: meta?.event_type || "",
      event_date: normalizeDateInput(meta?.date_label),
      location: meta?.location || "",
      notes: meta?.notes || "",
    });
  }, [meta]);

  useEffect(() => {
    const brief = planData?.event_brief || {};
    const readiness = planData?.readiness || {};
    setBriefDraft({
      summary: brief.summary || "",
      tone: brief.tone || "",
      good_fit: (brief.good_fit || []).join("\n"),
      watch_outs: (brief.watch_outs || []).join("\n"),
      bring: (brief.bring || []).join("\n"),
      book_ahead: (brief.book_ahead || []).join("\n"),
      planning_notes: (planData?.planning_notes || []).join("\n"),
    });
    setChecklistDraft((planData?.prep_checklist || []).map((item) => ({
      task: item.task || "",
      status: item.status || "todo",
      owner: item.owner || "",
      need_by: item.need_by || "",
      category: item.category || "prep",
    })));
    setReadinessDraft({
      to_book: (readiness.to_book || []).join("\n"),
      pending: (readiness.pending || []).join("\n"),
      confirmed: (readiness.confirmed || []).join("\n"),
      day_of: (readiness.day_of || []).join("\n"),
    });
    setShoppingAdded(false);
  }, [planData]);

  const runGenerate = async () => {
    setLoading(true);
    setConfirmAiGenerate(false);
    setError("");
    try {
      const data = await eventPlanApi.generate(eventType, eventId, { provider: user?.ai_provider || "openai" });
      setSavedPlanMeta(data.saved_plan || null);
      setPlanData(data.saved_plan?.plan_data || null);
      setMeta(data.meta || meta);
      setTravelerProfile(data.traveler_profile || travelerProfile);
      applyAiUsage(data.ai_usage);
      toast?.(hasPlan ? "Event plan refreshed" : "Event plan ready", "success");
    } catch (err) {
      setError(err.message || "Failed to build event plan.");
      toast?.(err.message || "Failed to build event plan.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = () => {
    setConfirmAiGenerate(true);
  };

  const handleAddEventShopping = async () => {
    if (eventShoppingItems.length === 0 || shoppingAdded) return;
    setAddingShopping(true);
    try {
      const result = await shoppingApi.bulkAdd(eventShoppingItems);
      const createdCount = result?.created_count ?? (Array.isArray(result) ? result.length : eventShoppingItems.length);
      const skippedCount = result?.skipped_count ?? 0;
      setShoppingAdded(true);
      if (createdCount > 0 && skippedCount > 0) {
        toast?.(`${createdCount} added, ${skippedCount} already in Shopping`, "success");
      } else if (createdCount > 0) {
        toast?.(`${createdCount} event items added to Shopping`, "success");
      } else {
        toast?.("Those event items are already in Shopping", "info");
      }
    } catch (err) {
      toast?.(err.message || "Failed to add shopping items.", "error");
    } finally {
      setAddingShopping(false);
    }
  };

  const createStarterPlan = async () => {
    const starterPlan = buildEventStarterPlan(meta);
    const saved = await savePlan(starterPlan, "Starter event plan created");
    if (saved) setActiveTab("prep");
  };

  const saveDetails = async () => {
    if (eventType !== "event") {
      navigate(eventType === "group" ? `/app/events?group_event_id=${eventId}` : "/app/events");
      return;
    }
    if (!detailsDraft.event_date) {
      setError("Add an occasion date before saving.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await eventsApi.updatePersonal(eventId, {
        title: detailsDraft.title.trim() || null,
        event_type: detailsDraft.event_type || meta?.event_type || "other_event",
        event_date: detailsDraft.event_date,
        location: detailsDraft.location.trim() || null,
        notes: detailsDraft.notes.trim() || null,
      });
      const data = await eventPlanApi.get(eventType, eventId);
      setMeta(data.meta || null);
      setTravelerProfile(data.traveler_profile || null);
      setSavedPlanMeta(data.saved_plan || null);
      setPlanData(data.saved_plan?.plan_data || null);
      setEditingDetails(false);
      toast?.("Occasion details saved. Create a starter plan or refresh AI when needed.", "success");
    } catch (err) {
      setError(err.message || "Failed to save occasion details.");
      toast?.(err.message || "Failed to save occasion details.", "error");
    } finally {
      setSaving(false);
    }
  };

  const savePlan = async (nextPlan, successMessage) => {
    setSaving(true);
    setError("");
    try {
      const data = await eventPlanApi.update(eventType, eventId, { plan_data: nextPlan });
      setSavedPlanMeta(data.saved_plan || null);
      setPlanData(data.saved_plan?.plan_data || null);
      setTravelerProfile(data.traveler_profile || travelerProfile);
      toast?.(successMessage, "success");
      return true;
    } catch (err) {
      setError(err.message || "Failed to save event plan.");
      toast?.(err.message || "Failed to save event plan.", "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveMoneyTracking = async () => {
    setSaving(true);
    setError("");
    try {
      const data = await eventPlanApi.update(eventType, eventId, moneyDraft);
      setSavedPlanMeta(data.saved_plan || null);
      setMoneyDraft({
        planned_budget: data.saved_plan?.planned_budget ?? "",
        actual_spend: data.saved_plan?.actual_spend ?? "",
      });
      toast?.("Event spending saved to Household Budget", "success");
    } catch (err) {
      setError(err.message || "Failed to save event spending.");
      toast?.(err.message || "Failed to save event spending.", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveBrief = async () => {
    const nextPlan = {
      ...(planData || {}),
      event_brief: {
        summary: briefDraft.summary,
        tone: briefDraft.tone,
        good_fit: parseLines(briefDraft.good_fit),
        watch_outs: parseLines(briefDraft.watch_outs),
        bring: parseLines(briefDraft.bring),
        book_ahead: parseLines(briefDraft.book_ahead),
      },
      planning_notes: parseLines(briefDraft.planning_notes),
    };
    await savePlan(nextPlan, "Event brief saved");
    setEditingBrief(false);
  };

  const saveChecklist = async () => {
    const nextPlan = {
      ...(planData || {}),
      prep_checklist: checklistDraft.map((item) => ({
        task: item.task,
        status: item.status,
        owner: item.owner,
        need_by: item.need_by,
        category: item.category || "prep",
      })).filter((item) => item.task),
    };
    await savePlan(nextPlan, "Checklist saved");
    setEditingChecklist(false);
  };

  const saveReadiness = async () => {
    const nextPlan = {
      ...(planData || {}),
      readiness: {
        to_book: parseLines(readinessDraft.to_book),
        pending: parseLines(readinessDraft.pending),
        confirmed: parseLines(readinessDraft.confirmed),
        day_of: parseLines(readinessDraft.day_of),
      },
    };
    await savePlan(nextPlan, "Readiness saved");
    setEditingReadiness(false);
  };

  const startChecklistEdit = () => {
    setEditingChecklist(true);
    setChecklistDraft((current) => current.length > 0 ? current : [{ task: "", status: "todo", owner: "", need_by: "", category: "prep" }]);
  };

  return (
    <div className="travel-page event-plan-page hm-desktop-page-shell">
      <div className="travel-back-row">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate("/app/events")}>← Events & Trips</button>
      </div>

      <div className="travel-hero travel-hero--compact">
        <div className="travel-hero-copy">
          <div className="travel-hero-eyebrow">✦ Event planner</div>
          <h2>{meta?.title || "Event plan"}</h2>
          <p>
            {meta?.context_label ? `${meta.context_label} · ` : ""}
            {formatDateLabel(meta?.date_label) || "Saved occasion plan"}
          </p>
          <div className="travel-pill-row">
            <div className="travel-pill">
              <span className="travel-pill-value" style={{ textTransform: "capitalize" }}>🎉 {eventLabel}</span>
            </div>
            <div className="travel-pill">
              <span className="travel-pill-value">{eventType === "group" ? "👥 Shared" : "👤 Personal"}</span>
            </div>
            {meta?.location ? (
              <div className="travel-pill">
                <span className="travel-pill-value">📍 {meta.location}</span>
              </div>
            ) : null}
            <div className="travel-pill">
              <span className="travel-pill-value">{hasPlan ? "✅ Saved" : "✨ Ready to build"}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="travel-tabs travel-tabs--mobile event-plan-tabs">
        {[
          { key: "details", icon: "✏️", label: "Details", sub: "Set up" },
          { key: "prep", icon: "✅", label: "Prep", sub: "Tasks" },
          { key: "plan", icon: "🗓", label: "Plan", sub: "Flow" },
          { key: "overview", icon: "📋", label: "Overview", sub: "Brief" },
        ].map((tab) => (
          <button key={tab.key} type="button" onClick={() => setActiveTab(tab.key)} className={`travel-tab-btn${activeTab === tab.key ? " is-active" : ""}`}>
            <span>{tab.icon}</span>
            <strong>{tab.label}</strong>
            <small>{tab.sub}</small>
          </button>
        ))}
      </div>

      {error && (
        <div className="notice-banner is-error mb-16">
          <p>{error}</p>
        </div>
      )}

      {loadingPlan && (
        <div style={{ textAlign: "center", padding: "18px 0", color: "var(--ink-muted)" }}>
          <span className="spinner" style={{ marginRight: 8 }} />
          Loading saved event plan…
        </div>
      )}

      {activeTab === "details" && !loadingPlan && (
        <>
          <div className="travel-tab-content-head travel-tab-content-head--card">
            <div>
              <h3>📋 Occasion details</h3>
              <p>{hasPlan ? "Review the source details before refreshing the plan." : "Start here so HouseMind can build a useful starter plan."}</p>
            </div>
            <div className="event-plan-detail-actions">
              {editingDetails ? (
                <>
                  <button
                    type="button"
                    className="travel-edit-plan-btn"
                    onClick={() => {
                      setEditingDetails(false);
                      setDetailsDraft({
                        title: meta?.title || "",
                        event_type: meta?.event_type || "",
                        event_date: normalizeDateInput(meta?.date_label),
                        location: meta?.location || "",
                        notes: meta?.notes || "",
                      });
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="button" className="travel-edit-plan-btn is-primary" onClick={saveDetails} disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="travel-edit-plan-btn"
                  onClick={() => eventType === "event" ? setEditingDetails(true) : navigate(`/app/events?group_event_id=${eventId}`)}
                >
                  ✏️ {eventType === "event" ? "Edit details" : "Open"}
                </button>
              )}
            </div>
          </div>

          <div className="travel-mobile-stats event-plan-stats">
            <div className="travel-stat-card">
              <div className="travel-stat-label">Prep tasks</div>
              <div className="travel-stat-value">{prepTotal || 0}</div>
              <div className="travel-stat-hint">{prepDone} done</div>
            </div>
            <div className="travel-stat-card">
              <div className="travel-stat-label">Timeline</div>
              <div className="travel-stat-value">{timelineCount || 0}</div>
              <div className="travel-stat-hint">moments</div>
            </div>
            <div className="travel-stat-card">
              <div className="travel-stat-label">{displayRules.showPantryShopping ? "Shopping" : "Supplies"}</div>
              <div className="travel-stat-value">{displayRules.showPantryShopping ? (shoppingCount || 0) : suppliesCount}</div>
              <div className="travel-stat-hint">{displayRules.showPantryShopping ? "suggested" : "planned"}</div>
            </div>
          </div>

          <div className="travel-overview-block">
            <div className="travel-overview-section-label">✦ Event source</div>
            <div className="travel-details-card">
              <div className="travel-details-row">
                <div className="travel-details-field">
                  <label>Occasion</label>
                  {editingDetails ? (
                    <input
                      className="form-input"
                      value={detailsDraft.title}
                      onChange={(e) => setDetailsDraft((current) => ({ ...current, title: e.target.value }))}
                      placeholder="Occasion name"
                    />
                  ) : (
                    <div className="event-plan-readonly-field">{meta?.title || "Event plan"}</div>
                  )}
                </div>
              </div>
              <div className="travel-details-row travel-details-row--split">
                <div className="travel-details-field">
                  <label>Type</label>
                  {editingDetails ? (
                    <select
                      className="form-select"
                      value={detailsDraft.event_type || "other_event"}
                      onChange={(e) => setDetailsDraft((current) => ({ ...current, event_type: e.target.value }))}
                    >
                      {eventTypeOptionsFor(detailsDraft.event_type || meta?.event_type).map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="event-plan-readonly-field" style={{ textTransform: "capitalize" }}>{eventLabel}</div>
                  )}
                </div>
                <div className="travel-details-field">
                  <label>Mode</label>
                  <div className="event-plan-readonly-field">{eventType === "group" ? "Shared event" : "Personal event"}</div>
                </div>
              </div>
              <div className="travel-details-row travel-details-row--split">
                <div className="travel-details-field">
                  <label>Date</label>
                  {editingDetails ? (
                    <input
                      className="form-input"
                      type="date"
                      value={detailsDraft.event_date}
                      onChange={(e) => setDetailsDraft((current) => ({ ...current, event_date: e.target.value }))}
                    />
                  ) : (
                    <div className="event-plan-readonly-field">{formatDateLabel(meta?.date_label) || "Not set"}</div>
                  )}
                </div>
                <div className="travel-details-field">
                  <label>Location</label>
                  {editingDetails ? (
                    <input
                      className="form-input"
                      value={detailsDraft.location}
                      onChange={(e) => setDetailsDraft((current) => ({ ...current, location: e.target.value }))}
                      placeholder="Venue, address, or area"
                    />
                  ) : (
                    <div className="event-plan-readonly-field">{meta?.location || "Not set"}</div>
                  )}
                </div>
              </div>
              <div className="travel-details-row">
                <div className="travel-details-field">
                  <label>Saved plan</label>
                  <div className="event-plan-readonly-field">
                    {savedPlanMeta?.updated_at ? `Updated ${new Date(savedPlanMeta.updated_at).toLocaleString()}` : "Not generated yet"}
                  </div>
                </div>
              </div>
              <div className="hm-source-money-card">
                <div>
                  <strong>Money tracking</strong>
                  <span>Optional amounts appear automatically in Household Budget.</span>
                </div>
                <div className="hm-source-money-grid">
                  <label><span>Planned budget</span><div className="hm-money-input"><i>$</i><input type="number" min="0" step="0.01" inputMode="decimal" value={moneyDraft.planned_budget} onChange={(e) => setMoneyDraft((current) => ({ ...current, planned_budget: e.target.value }))} placeholder="0.00" /></div></label>
                  <label><span>Actual spent</span><div className="hm-money-input"><i>$</i><input type="number" min="0" step="0.01" inputMode="decimal" value={moneyDraft.actual_spend} onChange={(e) => setMoneyDraft((current) => ({ ...current, actual_spend: e.target.value }))} placeholder="0.00" /></div></label>
                </div>
                <button type="button" className="btn btn-secondary btn-sm" onClick={saveMoneyTracking} disabled={saving}>{saving ? "Saving..." : "Save spending"}</button>
              </div>
              <div className="travel-details-row">
                <div className="travel-details-field">
                  <label>Planning brief</label>
                  {editingDetails ? (
                    <textarea
                      className="form-textarea"
                      rows={5}
                      value={detailsDraft.notes}
                      onChange={(e) => setDetailsDraft((current) => ({ ...current, notes: e.target.value }))}
                      placeholder={planningBriefPlaceholder(detailsDraft.event_type || meta?.event_type)}
                    />
                  ) : (
                    <div className="event-plan-readonly-field event-plan-readonly-field--textarea">
                      {meta?.notes || "Add occasion planning notes here, then refresh this plan."}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <EventStarterCard
            meta={meta}
            planData={planData}
            saving={saving}
            loading={loading}
            onCreateStarter={createStarterPlan}
            onGenerate={handleGenerate}
            onOpenPrep={() => setActiveTab("prep")}
          />
        </>
      )}

      {hasPlan && activeTab === "overview" && (
        <>
          <div className="travel-mobile-stats event-plan-stats">
            <div className="travel-stat-card">
              <div className="travel-stat-label">Prep tasks</div>
              <div className="travel-stat-value">{prepTotal || 0}</div>
              <div className="travel-stat-hint">{prepDone} done</div>
            </div>
            <div className="travel-stat-card">
              <div className="travel-stat-label">RSVPs</div>
              <div className="travel-stat-value">{meta?.rsvp_count || 0}</div>
              <div className="travel-stat-hint">responses</div>
            </div>
            <div className="travel-stat-card">
              <div className="travel-stat-label">Timeline</div>
              <div className="travel-stat-value">{timelineCount || 0}</div>
              <div className="travel-stat-hint">moments</div>
            </div>
          </div>

          {travelerProfile && (
            <div className="travel-overview-block">
              <div className="travel-overview-section-label">✦ Planned for this event</div>
              <div className="card travel-overview-personalized">
                <div className="travel-overview-ai-label">✦ HouseMind personalized</div>
                <p className="travel-overview-main-copy">{travelerProfile.summary}</p>
                {travelerHighlights.length > 0 && (
                  <div className="travel-overview-chip-row">
                    {travelerHighlights.map((item) => (
                      <span key={item} className="travel-overview-chip">{item}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <TravelSection eyebrow="Overview" title="Event overview" subtitle="The main details and ideas for this event.">
            {meta?.notes ? (
              <div className="card card-sm mb-16" style={{ background: "var(--cream)" }}>
                <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                  Planning brief
                </div>
                <p style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.6, margin: 0 }}>
                  {meta.notes}
                </p>
              </div>
            ) : (
              <div className="notice-banner mb-16">
                <p>Add an event vision from Events & Trips to guide future AI plans with vibe, dress code, guest count, budget, food ideas, and anything to avoid.</p>
              </div>
            )}
            <div className="flex-between mb-16" style={{ gap: 8 }}>
              <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>{editingBrief ? "Update this section, then save." : "Saved with this event."}</div>
              <div className="flex-center gap-8">
                {editingBrief ? (
                  <>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingBrief(false)} disabled={saving}>Cancel</button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={saveBrief} disabled={saving}>{saving ? "Saving..." : "Save brief"}</button>
                  </>
                ) : (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingBrief(true)}>Edit overview</button>
                )}
              </div>
            </div>

            {editingBrief ? (
              <div className="card" style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Summary</label>
                  <textarea className="form-textarea" rows={3} value={briefDraft.summary} onChange={(e) => setBriefDraft((current) => ({ ...current, summary: e.target.value }))} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Tone</label>
                  <input className="form-input" value={briefDraft.tone} onChange={(e) => setBriefDraft((current) => ({ ...current, tone: e.target.value }))} />
                </div>
                <div className="grid-2">
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Good fit</label>
                    <textarea className="form-textarea" rows={4} value={briefDraft.good_fit} onChange={(e) => setBriefDraft((current) => ({ ...current, good_fit: e.target.value }))} placeholder="One item per line" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Watch-outs</label>
                    <textarea className="form-textarea" rows={4} value={briefDraft.watch_outs} onChange={(e) => setBriefDraft((current) => ({ ...current, watch_outs: e.target.value }))} placeholder="One item per line" />
                  </div>
                </div>
                <div className="grid-2">
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">What to bring</label>
                    <textarea className="form-textarea" rows={4} value={briefDraft.bring} onChange={(e) => setBriefDraft((current) => ({ ...current, bring: e.target.value }))} placeholder="One item per line" />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Book ahead</label>
                    <textarea className="form-textarea" rows={4} value={briefDraft.book_ahead} onChange={(e) => setBriefDraft((current) => ({ ...current, book_ahead: e.target.value }))} placeholder="One item per line" />
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Planning notes</label>
                  <textarea className="form-textarea" rows={4} value={briefDraft.planning_notes} onChange={(e) => setBriefDraft((current) => ({ ...current, planning_notes: e.target.value }))} placeholder="One item per line" />
                </div>
              </div>
            ) : null}

            <div className="suggestions-grid">
              <div className="card card-sm">
                <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Quick read</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {planData?.event_brief?.summary ? <p style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.6, margin: 0 }}>{planData.event_brief.summary}</p> : null}
                  {planData?.event_brief?.tone ? <div style={{ fontSize: 14, color: "var(--ink)" }}><strong>Tone:</strong> {planData.event_brief.tone}</div> : null}
                </div>
              </div>
              <div className="card card-sm">
                <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Book ahead</div>
                {(planData?.event_brief?.book_ahead || []).length > 0 ? (planData.event_brief.book_ahead || []).map((item) => (
                  <div key={item} style={{ fontSize: 14, color: "var(--ink)", marginBottom: 8 }}>{item}</div>
                )) : <p style={{ fontSize: 14, color: "var(--ink-muted)", margin: 0 }}>Nothing urgent surfaced here yet.</p>}
              </div>
              <div className="card card-sm">
                <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Bring / watch for</div>
                {[...(planData?.event_brief?.bring || []), ...(planData?.event_brief?.watch_outs || [])].length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {(planData?.event_brief?.bring || []).map((item) => <div key={`bring-${item}`} style={{ fontSize: 14, color: "var(--ink)" }}>{item}</div>)}
                    {(planData?.event_brief?.watch_outs || []).map((item) => <div key={`watch-${item}`} style={{ fontSize: 13, color: "var(--ink-muted)" }}>{item}</div>)}
                  </div>
                ) : <p style={{ fontSize: 14, color: "var(--ink-muted)", margin: 0 }}>The planner did not surface extra watch-outs yet.</p>}
              </div>
            </div>

            {(planData?.event_brief?.good_fit || []).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
                {(planData.event_brief.good_fit || []).map((item) => (
                  <span key={item} className="badge" style={{ background: "var(--green-light)", color: "var(--green)" }}>{item}</span>
                ))}
              </div>
            )}
          </TravelSection>

          <TravelSection eyebrow="RSVPs" title="Guest responses" subtitle="Responses collected from the invite link.">
            <div className="mb-16">
              <RsvpPills meta={meta} />
            </div>
            {meta?.rsvp_count > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {(meta.rsvps || []).map((rsvp) => (
                  <div key={rsvp.id} className="card card-sm" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 700, color: "var(--ink)" }}>{rsvp.name}</div>
                      <div style={{ fontSize: 13, color: "var(--ink-muted)", marginTop: 4 }}>
                        {[rsvp.guest_count > 1 ? `${rsvp.guest_count} guests` : "1 guest", rsvp.dietary_notes].filter(Boolean).join(" · ")}
                      </div>
                      {rsvp.note ? <div style={{ fontSize: 13, color: "var(--ink-soft)", marginTop: 6 }}>{rsvp.note}</div> : null}
                    </div>
                    <span className="badge" style={{
                      background: rsvp.status === "in" ? "var(--green-light)" : rsvp.status === "maybe" ? "var(--gold-light)" : "var(--cream)",
                      color: rsvp.status === "in" ? "var(--green)" : rsvp.status === "maybe" ? "#8B6914" : "var(--ink-muted)",
                    }}>
                      {rsvp.status === "in" ? "Coming" : rsvp.status === "maybe" ? "Maybe" : "Not coming"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card card-sm" style={{ fontSize: 14, color: "var(--ink-muted)" }}>
                No RSVP responses yet. Create or copy the invite link from the Events & Trips card.
              </div>
            )}
          </TravelSection>
        </>
      )}

      {hasPlan && activeTab === "prep" && (
        <>
          <div className="travel-tab-content-head travel-tab-content-head--card">
            <div>
              <h3>✅ Prep list</h3>
              <p>{prepTotal ? `${prepDone} of ${prepTotal} tasks done · ${rsvpSummary}` : `${rsvpSummary} · no tasks yet`}</p>
            </div>
            <button type="button" className="travel-refresh-btn" onClick={handleGenerate} disabled={loading}>
              ✦ {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
          {hasEventShopping && (
            <TravelSection eyebrow="Food prep" title="Pantry-aware shopping" subtitle="What HouseMind thinks is covered at home and what still needs to be bought.">
              {eventShopping?.servings ? (
                <div className="card card-sm mb-16" style={{ background: "var(--cream)" }}>
                  <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                    Quantity guidance
                  </div>
                  <p style={{ fontSize: 14, color: "var(--ink-soft)", lineHeight: 1.6, margin: 0 }}>
                    {eventShopping.servings}
                  </p>
                </div>
              ) : null}

              <div className="suggestions-grid">
                <div className="card card-sm">
                  <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                    Already in pantry
                  </div>
                  {pantryMatches.length > 0 ? pantryMatches.map((item, index) => (
                    <div key={`${item.name || "pantry"}-${index}`} style={{ fontSize: 14, color: "var(--ink)", marginBottom: 10 }}>
                      <strong>{item.name}</strong>{item.quantity ? ` · ${item.quantity}` : ""}
                      {item.use_for ? <div style={{ fontSize: 13, color: "var(--ink-muted)", marginTop: 2 }}>{item.use_for}</div> : null}
                      {item.expiry_note ? <div style={{ fontSize: 13, color: "#9A5B00", marginTop: 2 }}>{item.expiry_note}</div> : null}
                    </div>
                  )) : <p style={{ fontSize: 14, color: "var(--ink-muted)", margin: 0 }}>No pantry matches found for this plan.</p>}
                </div>

                <div className="card card-sm">
                  <div className="flex-between" style={{ gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
                    <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Suggested shopping
                    </div>
                    {eventShoppingItems.length > 0 ? (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={handleAddEventShopping} disabled={addingShopping || shoppingAdded}>
                        {addingShopping ? "Adding..." : shoppingAdded ? "Added to Shopping" : "Add to Shopping"}
                      </button>
                    ) : null}
                  </div>
                  {eventShoppingItems.length > 0 ? eventShoppingItems.map((item, index) => {
                    const original = (eventShopping.shopping_items || [])[index] || {};
                    return (
                      <div key={`${item.name}-${index}`} style={{ fontSize: 14, color: "var(--ink)", marginBottom: 10 }}>
                        <strong>{item.name}</strong>{item.quantity ? ` · ${item.quantity}` : ""}{item.section ? ` · ${item.section}` : ""}
                        {original.reason ? <div style={{ fontSize: 13, color: "var(--ink-muted)", marginTop: 2 }}>{original.reason}</div> : null}
                      </div>
                    );
                  }) : <p style={{ fontSize: 14, color: "var(--ink-muted)", margin: 0 }}>No missing shopping items suggested yet.</p>}
                </div>

                <div className="card card-sm">
                  <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                    Prep notes
                  </div>
                  <TextList items={eventShopping?.prep_notes} />
                </div>
              </div>
            </TravelSection>
          )}
        </>
      )}

      {hasPlan && activeTab === "plan" && (
        <>
          <div className="travel-tab-content-head travel-tab-content-head--card">
            <div>
              <h3>🗺️ Event flow</h3>
              <p>{timelineCount ? `${timelineCount} timeline moments` : "Guest flow, food, supplies, and communication."}</p>
            </div>
            <button type="button" className="travel-refresh-btn" onClick={handleGenerate} disabled={loading}>
              ✦ {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {(hasGuestExperience || hasTimeline) && (
            <TravelSection eyebrow="Guest flow" title="Guest experience" subtitle="How the event should feel for people attending.">
              <div className="suggestions-grid">
                <div className="card card-sm">
                  <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Arrival</div>
                  <TextList items={planData?.guest_experience?.arrival} />
                </div>
                <div className="card card-sm">
                  <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Comfort</div>
                  <TextList items={planData?.guest_experience?.comfort} />
                </div>
                <div className="card card-sm">
                  <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Flow</div>
                  <TextList items={planData?.guest_experience?.flow} />
                </div>
              </div>
              {hasTimeline ? (
                <div className="card mt-16">
                  <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>Suggested timeline</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {(planData.timeline || []).map((item, index) => (
                      <div key={`${item.time || "phase"}-${index}`} style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 12, alignItems: "start" }}>
                        <div style={{ fontSize: 13, color: "var(--ink-muted)", fontWeight: 600 }}>{item.time || "Phase"}</div>
                        <div>
                          <div style={{ fontSize: 14, color: "var(--ink)", fontWeight: 600 }}>{item.activity}</div>
                          {item.notes ? <div style={{ fontSize: 13, color: "var(--ink-muted)", marginTop: 2 }}>{item.notes}</div> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </TravelSection>
          )}

          {(hasFoodDrink || hasSupplies || hasBudget || hasCommunication) && (
            <TravelSection
              eyebrow="Host details"
              title={hasBudget ? "Setup, food, and budget" : "Setup and guest details"}
              subtitle="Practical details for hosting and communicating."
            >
              <div className="suggestions-grid">
                {hasFoodDrink ? (
                  <div className="card card-sm">
                    <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Food & drink</div>
                    <TextList items={[...(planData?.food_drink?.menu_direction || []), ...(planData?.food_drink?.dietary_notes || []), ...(planData?.food_drink?.serving_notes || [])]} />
                  </div>
                ) : null}
                {hasSupplies ? (
                  <div className="card card-sm">
                    <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Supplies</div>
                    {(planData.supplies || []).map((item, index) => (
                      <div key={`${item.item}-${index}`} style={{ fontSize: 14, color: "var(--ink)", marginBottom: 8 }}>
                        {item.item}{item.quantity ? ` · ${item.quantity}` : ""}{item.category ? ` · ${item.category}` : ""}
                      </div>
                    ))}
                  </div>
                ) : null}
                {hasBudget ? (
                  <div className="card card-sm">
                    <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Budget</div>
                    {(planData?.budget || []).map((item, index) => (
                      <div key={`${item.item}-${index}`} style={{ fontSize: 14, color: "var(--ink)", marginBottom: 8 }}>
                        <strong>{item.item}</strong>{item.range ? ` · ${item.range}` : ""}
                        {item.notes ? <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>{item.notes}</div> : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
              {hasCommunication ? (
                <div className="card mt-16">
                  <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Guest communication</div>
                  {planData.communication.invite_note ? <p style={{ fontSize: 14, color: "var(--ink)", marginTop: 0 }}>{planData.communication.invite_note}</p> : null}
                  {planData.communication.reminder_note ? <p style={{ fontSize: 14, color: "var(--ink-muted)" }}>{planData.communication.reminder_note}</p> : null}
                  <TextList items={planData.communication.questions_to_answer} />
                </div>
              ) : null}
            </TravelSection>
          )}
        </>
      )}

      {hasPlan && activeTab === "prep" && (
        <>

          <TravelSection eyebrow="Checklist" title="To-do list" subtitle="Add and update the tasks you want to track for this event.">
            <div className="flex-between mb-16" style={{ gap: 8 }}>
              <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>
                {editingChecklist ? "Update tasks, owners, or dates, then save." : (hasChecklist ? "Saved with this event." : "No tasks saved yet.")}
              </div>
              <div className="flex-center gap-8">
                {editingChecklist ? (
                  <>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingChecklist(false)} disabled={saving}>Cancel</button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={saveChecklist} disabled={saving}>{saving ? "Saving..." : "Save checklist"}</button>
                  </>
                ) : (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={startChecklistEdit}>{hasChecklist ? "Edit tasks" : "Add tasks"}</button>
                )}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {!editingChecklist && !hasChecklist ? (
                <div className="card card-sm" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 14, color: "var(--ink-muted)" }}>
                    No tasks saved yet. Add the first task you want to keep with this event.
                  </div>
                  <button type="button" className="btn btn-primary btn-sm" onClick={startChecklistEdit}>
                    Add first task
                  </button>
                </div>
              ) : null}
              {(editingChecklist ? checklistDraft : checklistItems).map((item, index) => (
                <div key={`${item.task}-${index}`} className="card card-sm" style={{ display: "grid", gap: 10 }}>
                  {editingChecklist ? (
                    <>
                      <input className="form-input" value={item.task || ""} onChange={(e) => setChecklistDraft((current) => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, task: e.target.value } : entry))} placeholder="Task" />
                      <div className="grid-2">
                        <select className="form-select" value={item.status || "todo"} onChange={(e) => setChecklistDraft((current) => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, status: e.target.value } : entry))}>
                          <option value="todo">To do</option>
                          <option value="pending">Pending</option>
                          <option value="done">Done</option>
                        </select>
                        <input className="form-input" value={item.owner || ""} onChange={(e) => setChecklistDraft((current) => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, owner: e.target.value } : entry))} placeholder="Owner" />
                      </div>
                      <div className="grid-2">
                        <input className="form-input" value={item.need_by || ""} onChange={(e) => setChecklistDraft((current) => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, need_by: e.target.value } : entry))} placeholder="Need by" />
                        <input className="form-input" value={item.category || ""} onChange={(e) => setChecklistDraft((current) => current.map((entry, entryIndex) => entryIndex === index ? { ...entry, category: e.target.value } : entry))} placeholder="Category" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex-between" style={{ gap: 10 }}>
                        <div style={{ fontWeight: 600, color: "var(--ink)" }}>{item.task}</div>
                        <span className="badge" style={{ background: item.status === "done" ? "var(--green-light)" : item.status === "pending" ? "var(--gold-light)" : "var(--cream)", color: item.status === "done" ? "var(--green)" : item.status === "pending" ? "#8B6914" : "var(--ink-muted)" }}>
                          {item.status === "done" ? "Done" : item.status === "pending" ? "Pending" : "To do"}
                        </span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 13, color: "var(--ink-muted)" }}>
                        {item.owner ? <span>Owner: {item.owner}</span> : null}
                        {item.need_by ? <span>Need by: {item.need_by}</span> : null}
                        {item.category ? <span>{item.category}</span> : null}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {editingChecklist ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setChecklistDraft((current) => [...current, { task: "", status: "todo", owner: "", need_by: "", category: "prep" }])}>
                  Add task
                </button>
              ) : null}
            </div>
          </TravelSection>

          <TravelSection eyebrow="Status" title="Booking status" subtitle="Keep track of what still needs attention and what is already handled.">
            <div className="flex-between mb-16" style={{ gap: 8 }}>
              <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>
                {editingReadiness ? "Update these lists, then save." : (hasReadiness ? "Saved with this event." : "No status saved yet.")}
              </div>
              <div className="flex-center gap-8">
                {editingReadiness ? (
                  <>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingReadiness(false)} disabled={saving}>Cancel</button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={saveReadiness} disabled={saving}>{saving ? "Saving..." : "Save readiness"}</button>
                  </>
                ) : (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingReadiness(true)}>{hasReadiness ? "Update status" : "Add status"}</button>
                )}
              </div>
            </div>

            {editingReadiness ? (
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">To book</label>
                  <textarea className="form-textarea" rows={5} value={readinessDraft.to_book} onChange={(e) => setReadinessDraft((current) => ({ ...current, to_book: e.target.value }))} placeholder="One item per line" />
                </div>
                <div className="form-group">
                  <label className="form-label">Pending</label>
                  <textarea className="form-textarea" rows={5} value={readinessDraft.pending} onChange={(e) => setReadinessDraft((current) => ({ ...current, pending: e.target.value }))} placeholder="One item per line" />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirmed</label>
                  <textarea className="form-textarea" rows={5} value={readinessDraft.confirmed} onChange={(e) => setReadinessDraft((current) => ({ ...current, confirmed: e.target.value }))} placeholder="One item per line" />
                </div>
                <div className="form-group">
                  <label className="form-label">Day-of reminders</label>
                  <textarea className="form-textarea" rows={5} value={readinessDraft.day_of} onChange={(e) => setReadinessDraft((current) => ({ ...current, day_of: e.target.value }))} placeholder="One item per line" />
                </div>
              </div>
            ) : (
              <div className="suggestions-grid">
                {[
                  { key: "to_book", label: "To book", items: readinessSections.to_book },
                  { key: "pending", label: "Pending", items: readinessSections.pending },
                  { key: "confirmed", label: "Confirmed", items: readinessSections.confirmed },
                  { key: "day_of", label: "Day-of", items: readinessSections.day_of },
                ].map((section) => (
                  <div key={section.key} className="card card-sm">
                    <div style={{ fontSize: 13, color: "var(--ink-hint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>{section.label}</div>
                    {section.items.length > 0 ? section.items.map((item) => (
                      <div key={item} style={{ fontSize: 14, color: "var(--ink)", marginBottom: 8 }}>{item}</div>
                    )) : <p style={{ fontSize: 14, color: "var(--ink-muted)", margin: 0 }}>Nothing here yet.</p>}
                  </div>
                ))}
              </div>
            )}
          </TravelSection>
        </>
      )}

      {!hasPlan && activeTab !== "details" && !loading && !loadingPlan && (
        <EventStarterCard
          meta={meta}
          planData={planData}
          saving={saving}
          loading={loading}
          onCreateStarter={createStarterPlan}
          onGenerate={handleGenerate}
          onOpenPrep={() => setActiveTab("prep")}
        />
      )}

      {showShoppingNotice && (
        <SavingNotice title="Adding" message="Adding event items to Shopping..." />
      )}
      {loading && (
        <SavingNotice title="Building" message={hasPlan ? "Refreshing event plan..." : "Building event plan..."} />
      )}
      {confirmAiGenerate && (
        <div className="app-modal-backdrop" onClick={() => setConfirmAiGenerate(false)}>
          <div
            className="app-modal-card admin-confirm-modal"
            style={{ maxWidth: 480, width: "calc(100% - 32px)" }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="admin-confirm-title">{hasPlan ? "Generate richer event plan?" : "Use AI for this event plan?"}</h3>
            <p className="admin-confirm-message">
              This uses 1 AI request. Use the starter plan for checklist and readiness basics; generate AI when you want richer guest flow, menu, communication, and personalized planning.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmAiGenerate(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={runGenerate}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      {!editingBrief && !editingChecklist && !editingReadiness && !showShoppingNotice && !loading && !confirmAiGenerate ? (
        <EventPlanAskBar meta={meta} activeTab={activeTab} planData={planData} />
      ) : null}
    </div>
  );
}
